import React from "react";
import { ExternalLink } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { LeaveReviewButton, RatingSummary, ReviewCard } from "@/components/site/GoogleReviews";
import { useGoogleReviews } from "@/lib/useGoogleReviews";

export default function Reviews() {
  const data = useGoogleReviews();
  const reviews = data?.reviews || [];
  return (
    <div data-testid="reviews-page">
      <Seo title="Google Reviews | Psalm 30:2 Chiropractic" description="Read what patients and families say about Psalm 30:2 Chiropractic in Gardner, Kansas — and share your own experience on Google." />
      <PageHero eyebrow="Reviews" title={<>Stories from <span className="italic text-taupe">our families.</span></>}
        intro="Your words help other families in Gardner find care that treats the whole chain. Thank you for trusting us." />
      <section className="bg-linen py-20 lg:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-14">
            <RatingSummary data={data} testid="reviews-rating" />
            <LeaveReviewButton testid="reviews-leave-review" />
          </div>
          {data === null && <p className="text-taupe" data-testid="reviews-loading">Loading reviews…</p>}
          {data && reviews.length === 0 && (
            <p className="text-espressosoft/80 font-light text-lg max-w-xl" data-testid="reviews-empty">
              Have we helped you or your family? We'd be grateful if you shared your experience on Google.
            </p>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => <Reveal key={r.author + i} delay={i * 60}><ReviewCard r={r} i={i} /></Reveal>)}
          </div>
          {data?.maps_uri && (
            <a href={data.maps_uri} target="_blank" rel="noreferrer" data-testid="reviews-see-all"
              className="mt-12 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-espresso link-underline">
              See all reviews on Google Maps <ExternalLink size={13} />
            </a>
          )}
          {reviews.length > 0 && <p className="mt-6 text-xs text-taupe" data-testid="reviews-attribution">Reviews from Google Maps.</p>}
        </div>
      </section>
      <section className="bg-espresso text-ivory py-24 px-6 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-5xl mb-4">Share your story.</h2>
          <p className="text-ivory/65 font-light mb-10">It takes less than a minute and means the world to our small practice.</p>
          <LeaveReviewButton variant="light" testid="reviews-cta-leave-review" />
        </Reveal>
      </section>
    </div>
  );
}
